/**
 * 水印：基于 antd Watermark，结合当前登录用户生成文案与样式
 */

import { useMemo } from 'react';
import { Watermark } from 'antd';
import { getAdminInfo } from '@/utils/utils';

/** 与原先 canvas 三色方案对应的字体颜色（略提高不透明度以便 antd 单通道绘制下可读） */
const WATERMARK_FONT_COLORS = {
    gray: 'rgba(128, 128, 128, 0.28)',
    purple: 'rgba(147, 112, 219, 0.26)',
    aurora: 'rgba(0, 191, 255, 0.26)',
};

/**
 * 获取当前登录用户信息（复用 utils 中的 getAdminInfo）
 * @returns {Object|null} 用户信息对象
 */
export function getCurrentUserInfo() {
    const { adminId, adminName, adminPhone } = getAdminInfo();
    if (!adminId && !adminName && !adminPhone) return null;
    return { id: adminId || '', name: adminName || '', phone: adminPhone || '' };
}

/**
 * 生成水印文本内容
 * @param {Object} userInfo 用户信息
 * @returns {string} 水印文本
 */
export function generateWatermarkText(userInfo) {
    if (!userInfo) {
        console.warn('用户信息为空，无法生成水印文本');
        return '';
    }

    const { name, phone } = userInfo;
    if (!name && !phone) {
        console.warn('用户姓名和手机号均为空，无法生成水印文本');
        return '';
    }

    const parts = [];
    if (name) parts.push(name);
    if (phone) parts.push(phone);

    const text = parts.join(' ');
    console.log('生成的水印文本:', text);
    return text;
}

/**
 * 根据用户ID生成水印样式类型
 * @param {string|number} userId 用户ID
 * @returns {string} 水印样式类型 ('gray' | 'purple' | 'aurora')
 */
export function getWatermarkStyleType(userId) {
    if (!userId) {
        return 'gray';
    }

    const hash = String(userId)
        .split('')
        .reduce((acc, char) => {
            return acc + char.charCodeAt(0);
        }, 0);

    const styles = ['gray', 'purple', 'aurora'];
    return styles[hash % 3];
}

/**
 * 获取水印样式类名（历史兼容，供 Less 等使用）
 * @param {string|number} userId 用户ID
 * @returns {string} CSS类名
 */
export function getWatermarkClassName(userId) {
    const styleType = getWatermarkStyleType(userId);
    return `watermark-${styleType}`;
}

/**
 * 检查是否应该显示水印
 * @returns {boolean} 是否显示水印
 */
export function shouldShowWatermark() {
    return true;
}

/**
 * 构造 antd Watermark 的 props；无法展示时返回 null
 * @returns {import('antd').WatermarkProps | null}
 */
export function getAntdWatermarkProps() {
    if (!shouldShowWatermark()) {
        return null;
    }

    const userInfo = getCurrentUserInfo();
    if (!userInfo) {
        console.warn('无法获取用户信息，跳过水印应用');
        return null;
    }

    const content = generateWatermarkText(userInfo);
    if (!content) {
        console.warn('水印文本为空，跳过水印应用');
        return null;
    }

    const color = WATERMARK_FONT_COLORS.gray;

    return {
        content,
        rotate: -30,
        gap: [120, 140],
        font: { color, fontSize: 14 },
        zIndex: 9,
    };
}

/**
 * 在会话内缓存一份水印配置（与原先 PrivateLayout 挂载时读取一次的行为一致）
 * @returns {import('antd').WatermarkProps | null}
 */
export function useAntdWatermarkProps() {
    return useMemo(() => getAntdWatermarkProps(), []);
}

/**
 * 主内容区水印容器：无有效配置时原样渲染子节点
 * @param {{ children: import('react').ReactNode }} props
 */
export function UserWatermark({ children }) {
    const watermarkProps = useAntdWatermarkProps();

    if (!watermarkProps) {
        return children;
    }

    // Watermark 根默认 overflow:hidden；主区纵向滚动见 main.main-container（App.less/.user-watermark-container）。
    return (
        <Watermark {...watermarkProps} className="user-watermark-container">
            {children}
        </Watermark>
    );
}

export { Watermark };
